/**
 * TeamSlotGrid — the slot picker's Team mode grid: ONE day, one column per
 * recruiter, side by side on a shared time axis (the look of My Calendar's
 * Team day, components/calendar/TeamDayView.tsx). Each column is the Week
 * view's SlotColumn over that recruiter's availability read, so the cells,
 * blocks, ghost, selection and now-line behave exactly as they do there; a
 * click picks the recruiter AND the time (CallbackSlotPicker owns the state).
 *
 * The grid scrolls inside the dialog both ways: the names stay pinned above
 * and the hours pinned to the left however many recruiters are shown.
 */
import { AlertTriangle, Loader2, UserMinus } from 'lucide-react';
import { TeamColumn } from '../calendar/teamDayLogic';
import { personColor } from '../calendar/colors';
import { DayColumn, ROW_PX, busyTitle, px } from './SlotColumn';
import { AvailabilityResponse, BookingWindow, GridBounds, GridCell, dayCells, hourLabel, isAbsentDay } from './slotLogic';
import { busyLanes } from './teamSlotLogic';

export interface TeamColumnState {
  data: AvailabilityResponse | null;
  loading: boolean;
  error: boolean;
}

interface Props {
  day: Date;
  columns: TeamColumn[];
  stateOf: (id: string) => TeamColumnState;
  bounds: GridBounds;
  w: BookingWindow;
  dur: number;
  dayKeyOf: (ms: number) => string;
  /** The day is one of the office's closed dates: every column greyed. */
  closed: boolean;
  now: number;
  isPhone: boolean;
  hover: { col: string; ms: number } | null;
  setHover: (next: (prev: { col: string; ms: number } | null) => { col: string; ms: number } | null) => void;
  sel: { assignee: string; ms: number; conflicted: boolean } | null;
  current: { start: string; durationMin: number; assignee: string | null } | null;
  onChoose: (columnId: string, cell: GridCell) => void;
}

export default function TeamSlotGrid({
  day,
  columns,
  stateOf,
  bounds,
  w,
  dur,
  dayKeyOf,
  closed,
  now,
  isPhone,
  hover,
  setHover,
  sel,
  current,
  onChoose,
}: Props) {
  const colMin = isPhone ? '8rem' : '9rem';
  const gridCols = { gridTemplateColumns: `3.25rem repeat(${Math.max(columns.length, 1)}, minmax(${colMin}, 1fr))` };
  const height = bounds.rows * ROW_PX;
  const hours: number[] = [];
  for (let m = Math.ceil(bounds.startMin / 60) * 60; m < bounds.endMin; m += 60) hours.push(m);

  return (
    <div className="grid" style={gridCols} data-slot-grid>
      {/* Names, pinned to the top while the hours scroll. */}
      <div className="sticky left-0 top-0 z-30 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" />
      {columns.map((col) => {
        const id = col.id as string;
        const st = stateOf(id);
        const out = !!st.data && isAbsentDay(day, st.data.absent_days, dayKeyOf);
        return (
          <div
            key={`h-${id}`}
            className="sticky top-0 z-20 flex min-w-0 items-center gap-1.5 border-b border-l border-gray-200 bg-white px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800"
            title={col.name}
          >
            <span className={`h-2 w-2 flex-shrink-0 rounded-full ${personColor(id).dot}`} aria-hidden="true" />
            <span className="min-w-0 truncate text-xs font-medium text-gray-800 dark:text-gray-100">
              {col.name}
              {col.isMe && <span className="ml-1 font-normal text-gray-400">(you)</span>}
            </span>
            {st.loading && (
              <Loader2 className="ml-auto h-3 w-3 flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-label={`Loading ${col.name}'s calendar`} />
            )}
            {st.error && (
              <AlertTriangle className="ml-auto h-3 w-3 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-label={`Couldn't load ${col.name}'s calendar`} />
            )}
            {out && (
              <span className="ml-auto inline-flex flex-shrink-0 items-center gap-0.5 rounded border border-dashed border-gray-400 px-1 text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:border-gray-500 dark:text-gray-400">
                <UserMinus className="h-3 w-3" aria-hidden="true" />
                Out
              </span>
            )}
          </div>
        );
      })}

      {/* Hours, pinned to the left while the columns scroll. */}
      <div className="sticky left-0 z-10 bg-white dark:bg-gray-800" style={{ height }} aria-hidden="true">
        {hours.map((m) => (
          <span
            key={m}
            className="absolute right-1.5 -translate-y-1/2 whitespace-nowrap text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
            style={{ top: px(m - bounds.startMin) }}
          >
            {m === bounds.startMin ? '' : hourLabel(m)}
          </span>
        ))}
      </div>

      {columns.map((col) => {
        const id = col.id as string;
        const st = stateOf(id);
        const busy = st.data?.busy || [];
        const absent = st.data?.absent_days || [];
        return (
          <DayColumn
            key={`c-${id}`}
            day={day}
            cells={dayCells(day, bounds, w, dur, busy, absent, dayKeyOf, now)}
            height={height}
            bounds={bounds}
            busy={busy}
            laid={busyLanes(busy, day, bounds, busyTitle)}
            who={col.name}
            dur={dur}
            greyed={closed || isAbsentDay(day, absent, dayKeyOf)}
            loading={st.loading}
            now={now}
            hover={hover && hover.col === id ? hover.ms : null}
            setHover={(ms) => setHover((prev) => (ms === null ? (prev && prev.col === id ? null : prev) : { col: id, ms }))}
            sel={sel && sel.assignee === id ? { ms: sel.ms, conflicted: sel.conflicted } : null}
            current={current && current.assignee === id ? current : null}
            onChoose={(cell) => onChoose(id, cell)}
          />
        );
      })}
    </div>
  );
}
